import type { FontSelectionMap, FontStyleMap, FontWeightSelectionMap } from '../types';

export const fonts: FontSelectionMap = {
  inter: 'Inter',
  libre_caslon_text: 'Libre Caslon Text',
  space_mono: 'Space Mono',
  quicksand: 'Quicksand',
};

export const fontWeights: FontWeightSelectionMap = {
  thin: 100,
  extra_light: 200,
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extra_bold: 800,
  black: 900,
};

export const fontSizes = [13, 16, 20, 24, 32, 40, 48];

export const lineHeights: FontStyleMap = {
  none: {
    title: 'none',
    label: '1',
    styles: {
      lineHeight: 1,
    },
  },
  tight: {
    title: 'tight',
    label: '1.25',
    styles: {
      lineHeight: 1.25,
    },
  },
  snug: {
    title: 'snug',
    label: '1.375',
    styles: {
      lineHeight: 1.375,
    },
  },
  normal: {
    title: 'normal',
    label: '1.5',
    styles: {
      lineHeight: 1.5,
    },
  },
  relaxed: {
    title: 'relaxed',
    label: '1.625',
    styles: {
      lineHeight: 1.625,
    },
  },
  loose: {
    title: 'loose',
    label: '2 ',
    styles: {
      lineHeight: 2,
    },
  },
};

export const letterSpacings: FontStyleMap = {
  minus2p: {
    title: '-2%',
    label: '-0.02',
    styles: {
      letterSpacing: '-0.02em',
    },
  },
  minus1p: {
    title: '-1%',
    label: '-0.01',
    styles: {
      letterSpacing: '-0.01em',
    },
  },
  base: {
    title: 'base',
    label: '0',
    styles: {
      letterSpacing: '0em',
    },
  },
  plus1p: {
    title: '1%',
    label: '0.01',
    styles: {
      letterSpacing: '0.01em',
    },
  },
  plus2p: {
    title: '2%',
    label: '0.02',
    styles: {
      letterSpacing: '0.02em',
    },
  },
};

export const typeScales: FontStyleMap = {
  xs: {
    title: 'xs',
    label: '12/16',
    styles: {
      fontSize: 12,
      lineHeight: 16 / 12,
    },
  },
  sm: {
    title: 'sm',
    label: '14/20',
    styles: {
      fontSize: 14,
      lineHeight: 20 / 14,
    },
  },
  base: {
    title: 'base',
    label: '16/24',
    styles: {
      fontSize: 16,
      lineHeight: 24 / 16,
    },
  },
  lg: {
    title: 'lg',
    label: '18/28',
    styles: {
      fontSize: 18,
      lineHeight: 28 / 18,
    },
  },
  xl: {
    title: 'xl',
    label: '20/28',
    styles: {
      fontSize: 20,
      lineHeight: 28 / 20,
    },
  },
  '2xl': {
    title: '2xl',
    label: '24/32',
    styles: {
      fontSize: 24,
      lineHeight: 32 / 24,
    },
  },
  '3xl': {
    title: '3xl',
    label: '30/36',
    styles: {
      fontSize: 30,
      lineHeight: 36 / 30,
    },
  },
  '4xl': {
    title: '4xl',
    label: '36/40',
    styles: {
      fontSize: 36,
      lineHeight: 40 / 36,
    },
  },
  '5xl': {
    title: '5xl',
    label: '48/48',
    styles: {
      fontSize: 48,
      lineHeight: 1,
    },
  },
  '6xl': {
    title: '6xl',
    label: '60/60',
    styles: {
      fontSize: 60,
      lineHeight: 1,
    },
  },
  '7xl': {
    title: '7xl',
    label: '72/72',
    styles: {
      fontSize: 72,
      lineHeight: 1,
    },
  },
  '8xl': {
    title: '8xl',
    label: '96/96',
    styles: {
      fontSize: 96,
      lineHeight: 1,
    },
  },
  '9xl': {
    title: '9xl',
    label: '128/128',
    styles: {
      fontSize: 128,
      lineHeight: 1,
    },
  },
};

export const fontStyles: FontStyleMap = {
  title: {
    title: 'Title',
    styles: {
      fontSize: 80,
      color: 'default',
      fontWeight: 800,
      letterSpacing: -2.5,
      lineHeight: '90px',
    },
  },
  heading1: {
    title: 'Heading 1',
    styles: {
      fontSize: 56,
      fontWeight: 800,
      letterSpacing: '-2.5px',
      lineHeight: 1.25,
    },
  },
  heading2: {
    title: 'Heading 2',
    styles: {
      fontSize: 36,
      fontWeight: 800,
      letterSpacing: '-1px',
      lineHeight: 1.25,
    },
  },
  heading3: {
    title: 'Heading 3',
    styles: {
      fontSize: 26,
      fontWeight: 700,
      letterSpacing: '-0.5px',
      lineHeight: 1.25,
    },
  },
  heading4: {
    title: 'Heading 4',
    styles: {
      fontSize: 24,
      fontWeight: 600,
      letterSpacing: '-0.5px',
      lineHeight: 1.4,
    },
  },
  heading5: {
    title: 'Heading 5',
    styles: {
      fontSize: 20,
      fontWeight: 500,
      letterSpacing: '-0.25px',
      lineHeight: 1.4,
    },
  },
  heading6: {
    title: 'Heading 6',
    styles: {
      fontSize: 16,
      fontWeight: 400,
      letterSpacing: '0px',
      lineHeight: 1.5,
    },
  },
  paragraph: {
    title: 'Paragraph',
    styles: {
      fontSize: 16,
      color: 'default',
    },
  },
  small: {
    title: 'Small Text',
    styles: {
      fontSize: 13,
      color: 'default',
    },
  },
};

export const DEFAULT_FONT_SIZE = 16;
export const DEFAULT_FONT_WEIGHT = 400;
export const DEFAULT_LETTER_SPACING = 0;
